import React, { forwardRef, ReactNode, useEffect, useRef, useState } from 'react';
import { Animated, Platform, Pressable, PressableProps, StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { colors, interaction } from '../theme/tokens';
import type { FocusDestinations, FocusDirection, FocusRole } from '../tvNavigation/core';
import {
    createFocusInstanceId,
    getFocusModality,
    noteActivated,
    noteBlurred,
    noteFocused,
    registerFocusNode,
    setFocusModality,
    subscribeFocusTransitions,
} from '../tvNavigation/registry';

type Props = Omit<PressableProps, 'children' | 'style' | 'onFocus' | 'onBlur'> & {
    children: ReactNode | ((focused: boolean, hovered: boolean) => ReactNode);
    style?: StyleProp<ViewStyle> | ((focused: boolean, pressed: boolean, hovered: boolean) => StyleProp<ViewStyle>);
    onFocus?: PressableProps['onFocus'];
    onBlur?: PressableProps['onBlur'];
    accessibilityLabel?: string;
    accessibilityRole?: string;
    onFocusVisibleChange?: (visible: boolean) => void;
    onControllerPress?: () => void;
    onControllerMove?: (direction: FocusDirection) => boolean | void;
    onControllerBack?: () => boolean | void;
    /** TV focus priority — the element the D-pad lands on first. */
    tvPreferredFocus?: boolean;
    /** Explicit native-TV focus destinations. Web ignores these handles. */
    nextFocusUp?: number;
    nextFocusDown?: number;
    nextFocusLeft?: number;
    nextFocusRight?: number;
    /** Stable semantic identity used by the shared Web/TV focus resolver. */
    focusId?: string;
    focusGroup?: string;
    focusRole?: FocusRole;
    focusOrder?: number;
    focusDestinations?: FocusDestinations;
    /** Pointer/desktop shelf affordances are excluded from controller focus. */
    controllerFocusable?: boolean;
    /** Disable the shared full-control halo when a component owns an inner focus treatment. */
    focusHalo?: boolean;
};

function inferredSemantics(testID?: string): { group: string; role: FocusRole } {
    if (testID === 'tv-rail-control') return { group: 'shell.rail', role: 'rail' };
    if (testID === 'tv-hero-control') return { group: 'route.hero', role: 'hero' };
    if (testID?.startsWith('media-card-') || testID?.startsWith('landscape-media-card-') || testID?.startsWith('genre-card-')) {
        return { group: 'route.cards', role: 'card' };
    }
    return { group: 'route.controls', role: 'control' };
}

export const FocusablePressable = forwardRef<View, Props>(function FocusablePressable(
    {
        children, style, onFocus, onBlur, onFocusVisibleChange, onControllerPress, onControllerMove, onControllerBack, onHoverIn, onHoverOut, onPressIn, onPress,
        accessibilityLabel, accessibilityRole,
        tvPreferredFocus, focusId, focusGroup, focusRole, focusOrder, focusDestinations, controllerFocusable = true, focusHalo: showFocusHalo = true, ...props
    },
    ref,
) {
    const [focused, setFocused] = useState(false);
    const [hovered, setHovered] = useState(false);
    const focusProgress = useRef(new Animated.Value(0)).current;
    const localRef = useRef<View | null>(null);
    const generatedId = useRef(createFocusInstanceId(focusId ?? props.testID ?? accessibilityLabel));
    const semanticId = focusId ?? generatedId.current;
    const inferred = inferredSemantics(props.testID);
    const group = focusGroup ?? inferred.group;
    const role = focusRole ?? inferred.role;
    const destinationsKey = JSON.stringify(focusDestinations ?? {});

    useEffect(() => {
        Animated.timing(focusProgress, {
            toValue: focused ? 1 : 0,
            duration: focused ? interaction.focusEnter : interaction.focusExit,
            useNativeDriver: true,
        }).start();
    }, [focused, focusProgress]);

    useEffect(() => {
        if (!localRef.current) return undefined;
        return registerFocusNode({
            id: semanticId,
            group,
            role,
            order: focusOrder,
            destinations: focusDestinations,
            preferred: tvPreferredFocus,
            pointerOnly: !controllerFocusable,
            disabled: Boolean(props.disabled) || props.focusable === false,
            activate: onPress ? () => {
                if (onControllerPress) onControllerPress();
                else onPress({ stopPropagation: () => undefined, preventDefault: () => undefined } as any);
            } : undefined,
            move: onControllerMove,
            back: onControllerBack ? () => { onControllerBack(); return true; } : undefined,
            handle: localRef.current,
        });
    // The serialized key keeps inline destination objects from unregistering
    // the active control on every render while still applying graph changes.
    }, [destinationsKey, focusOrder, group, props.disabled, props.focusable, role, semanticId, tvPreferredFocus]);

    // React Native Web can move the semantic focus graph without delivering a
    // DOM focus event to the wrapped Pressable. Mirror graph ownership into
    // this component so controller focus runs the same animations as native
    // TV and keyboard focus.
    useEffect(() => {
        // Native TV delivers focus/blur directly from Pressable. The global
        // transition mirror is needed for React Native Web's DOM focus gaps;
        // subscribing every TV card only adds callback fan-out on navigation.
        if (Platform.OS !== 'web') return undefined;
        return subscribeFocusTransitions((transition) => {
        if (transition.toId === semanticId && transition.modality !== 'pointer') {
            setFocused(true);
            onFocusVisibleChange?.(true);
        } else if (transition.fromId === semanticId && transition.toId !== semanticId) {
            setFocused(false);
            onFocusVisibleChange?.(false);
        }
        });
    }, [onFocusVisibleChange, semanticId]);

    const assignRef = (value: View | null) => {
        localRef.current = value;
        if (typeof ref === 'function') ref(value);
        else if (ref) ref.current = value;
    };
    return (
        <Pressable
            {...props}
            accessibilityLabel={Platform.OS === 'web' ? undefined : accessibilityLabel}
            accessibilityRole={Platform.OS === 'web' ? undefined : accessibilityRole}
            {...(Platform.OS === 'web' ? { 'aria-label': accessibilityLabel, role: accessibilityRole } : {}) as any}
            ref={assignRef}
            focusable={props.focusable ?? true}
            hasTVPreferredFocus={tvPreferredFocus}
            onPressIn={(event) => {
                // A gamepad activation is delivered as a synthetic DOM click
                // on Web. Do not turn that into pointer modality here: doing
                // so hides the controller action layer before its handler can
                // run. Real pointer input is classified by the global
                // pointerdown listener below.
                onPressIn?.(event);
            }}
            onHoverIn={(event) => { if (Platform.OS === 'web') setFocusModality('pointer'); setHovered(true); onHoverIn?.(event); }}
            onHoverOut={(event) => { setHovered(false); onHoverOut?.(event); }}
            onFocus={(event) => {
                const visible = Platform.OS !== 'web' || getFocusModality() !== 'pointer';
                setFocused(visible);
                onFocusVisibleChange?.(visible);
                noteFocused(semanticId, Platform.OS !== 'web');
                onFocus?.(event);
            }}
            onBlur={(event) => { setFocused(false); onFocusVisibleChange?.(false); noteBlurred(semanticId); onBlur?.(event); }}
            onPress={(event) => { noteActivated(semanticId); onPress?.(event); }}
            style={({ pressed }) => typeof style === 'function'
                ? [webOutlineReset, style(focused, pressed, hovered)]
                : [webOutlineReset, style, focused && showFocusHalo && focusRing]}
        >
            {showFocusHalo ? <Animated.View
                pointerEvents="none"
                style={[
                    focusHalo,
                    {
                        opacity: focusProgress,
                        transform: [{ scale: focusProgress.interpolate({ inputRange: [0, 1], outputRange: [1.025, 1] }) }],
                    },
                ]}
            /> : null}
            {typeof children === 'function' ? children(focused, hovered) : children}
        </Pressable>
    );
});

// React Native Web otherwise adds a rectangular browser outline around the
// DOM pressable. Every FocusablePressable supplies its own platform-consistent
// focus treatment, so the browser ring is redundant and visually incorrect.
export const webOutlineReset = Platform.OS === 'web'
    ? ({ outlineStyle: 'none', outlineWidth: 0, outlineColor: 'transparent' } as unknown as ViewStyle)
    : undefined;

const focusHalo: ViewStyle = {
    ...StyleSheet.absoluteFillObject,
    borderWidth: 2,
    borderColor: colors.brandBright,
    borderRadius: 8,
    shadowColor: colors.brand,
    shadowOpacity: 0.62,
    shadowRadius: 10,
    elevation: interaction.raisedElevation,
    zIndex: 20,
};

// Web focus treatment is opt-in for living-room navigation. A pointer click
// may still move DOM focus, but must not make that control look TV-focused.
if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.addEventListener('keydown', (event) => {
        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
            setFocusModality('keyboard');
        }
    }, true);
    window.addEventListener('pointerdown', () => { setFocusModality('pointer'); }, true);
    window.addEventListener('mousedown', () => { setFocusModality('pointer'); }, true);
}

const focusRing: ViewStyle = {
    borderWidth: interaction.focusBorderWidth,
    borderColor: colors.brandBright,
    shadowColor: colors.brand,
    shadowOpacity: 0.58,
    shadowRadius: 8,
    elevation: interaction.raisedElevation,
    transform: [{ scale: interaction.focusScale }],
    zIndex: 10,
};
